import moment from 'moment'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import TripPanel from './TripPanel'

import Clickable from 'components/Common/Clickable'
import IconButton from 'components/Luxkit/Button/IconButton'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { sortBy } from 'lib/arrayUtils'
import { BasicTrip } from 'tripPlanner/types/common'

interface Props {
  trips: BasicTrip[]
  onCreateSelect?: () => void
  onSelectTrip: (trip: BasicTrip) => void
}

const Panel = styled(Clickable)`
  display: flex;
  width: 100%;
  align-items: center;
  padding: ${rem(12)};
  gap: ${rem(12)};
  border-radius: ${(props) => props.theme.borderRadius.medium};
  transition: background-color 0.2s;
  background-color: ${(props) => props.theme.palette.neutral.eight};

  &:hover {
    background-color: ${({ theme }) => theme.palette.neutral.seven};
  }
`

function TripSelect({ trips, onCreateSelect, onSelectTrip }: Props) {
  return config.TRIP_PLANNER_ENABLE_V2
    ? TripSelectV2({ trips, onCreateSelect, onSelectTrip })
    : TripSelectV1({ trips, onCreateSelect, onSelectTrip })
}

function TripSelectV1({ trips, onCreateSelect, onSelectTrip }: Props) {
  const [currentAndFutureTrips, pastTrips] = useMemo(() => {
    return [
      trips.filter((trip) => !trip.endDate || trip.endDate > moment()),
      trips.filter((trip) => trip.endDate && trip.endDate < moment()),
    ]
  }, [trips])

  return (
    <div data-testid="save-to-trip-modal">
      {currentAndFutureTrips.map((trip) => (
        <TripPanel key={trip.id} onSelectTrip={onSelectTrip} trip={trip} />
      ))}
      {pastTrips.map((trip) => (
        <TripPanel key={trip.id} onSelectTrip={onSelectTrip} trip={trip} />
      ))}
      {onCreateSelect && (
        <Panel onClick={onCreateSelect}>
          <IconButton
            size="large"
            kind="primary"
            shape="square"
            nonInteractable
          >
            <LinePlusIcon />
          </IconButton>
          <BodyText variant="large">Create new trip</BodyText>
        </Panel>
      )}
    </div>
  )
}

function TripSelectV2({ trips, onCreateSelect, onSelectTrip }: Props) {
  const sortedTrips = useMemo(() => {
    return [...sortBy(trips, (trip) => trip.interactedAt, 'desc')]
  }, [trips])

  return (
    <div data-testid="save-to-trip-modal">
      <Group direction="vertical" verticalAlign="start">
        <Group direction="vertical">
          <BodyText variant="large">
            Choose which trip you would like to save this offer to.
          </BodyText>
          {onCreateSelect && (
            <Panel onClick={onCreateSelect}>
              <IconButton
                size="large"
                kind="secondary"
                shape="square"
                nonInteractable
                variant="dark"
              >
                <LinePlusIcon />
              </IconButton>
              <BodyText variant="large">Create new trip</BodyText>
            </Panel>
          )}
        </Group>
        {sortedTrips.map((trip) => (
          <TripPanel key={trip.id} onSelectTrip={onSelectTrip} trip={trip} />
        ))}
      </Group>
    </div>
  )
}

export default TripSelect
